import React, { useEffect, useState } from "react";
import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { navigate } from "../../lib/router";
import { useLanguage } from "../../contexts/LanguageContext";
import { settingsLabels } from "./labels";
import { ArrowLeft, Plus, Save, Trash2, TrendingUp } from "lucide-react";
import { SectionCard } from "../../components/SectionCard";
import { toast } from "sonner";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../components/ui/select";

interface Specialty {
  id: number;
  name: string;
  room_id?: number | null;
}

interface Room {
  id: number;
  name: string;
}

export default function SpecialtiesListPage() {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].specialties;
  const [items, setItems] = useState<Specialty[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);

  // Pricing state: { specialtyId: { frequency: priceInCents } }
  const [prices, setPrices] = useState<Record<string, Record<number, string>>>({});

  const load = async () => {
    setLoading(true);
    const res = await api.listCompanySpecialties();
    if (res.ok) {
      setItems(Array.isArray(res.data) ? res.data : []);
    }
    
    const roomsRes = await api.listCompanyRooms();
    if (roomsRes.ok) {
      setRooms(Array.isArray(roomsRes.data) ? roomsRes.data : []);
    }
    
    // Load prices from localStorage
    const savedPrices = localStorage.getItem("specialty_pricing");
    if (savedPrices) {
      try {
        setPrices(JSON.parse(savedPrices));
      } catch (e) {
        console.error("Failed to parse specialty pricing", e);
      }
    } else {
      // Default initial prices
      setPrices({
        "default": {
          1: "198,00",
          2: "330,00",
          3: "450,00",
          4: "550,00"
        }
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handlePriceChange = (specId: string, freq: number, value: string) => {
    setPrices(prev => ({
      ...prev,
      [specId]: {
        ...(prev[specId] || prev["default"] || {}),
        [freq]: value
      }
    }));
  };

  const savePrices = () => {
    localStorage.setItem("specialty_pricing", JSON.stringify(prices));
    toast.success(lang === 'pt' ? "Configurações de valores salvas!" : "Pricing configurations saved!");
  };

  const deleteSpecialty = async (id: number) => {
    if (!confirm(lang === 'pt' ? "Tem certeza que deseja excluir esta especialidade?" : "Are you sure you want to delete this specialty?")) return;
    const res = await api.deleteCompanySpecialty(id);
    if (res.ok) {
      toast.success(lang === 'pt' ? "Especialidade excluída" : "Specialty deleted");
      load();
    }
  };

  const updateRoom = async (specId: number, roomId: string) => {
    const rid = roomId === "none" ? null : Number(roomId);
    const res = await api.updateCompanySpecialty(specId, { room_id: rid });
    if (res.ok) {
      toast.success(lang === 'pt' ? "Sala atualizada!" : "Room updated!");
      setItems(prev => prev.map(item => item.id === specId ? { ...item, room_id: rid } : item));
    } else {
      toast.error(lang === 'pt' ? "Erro ao atualizar sala" : "Error updating room");
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl p-6 space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/settings")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">{t.header}</h1>
            <p className="text-sm text-neutral-500">{lang === 'pt' ? "Gerencie suas especialidades e configure os valores para planos mensais." : "Manage your specialties and configure monthly plan pricing."}</p>
          </div>
        </div>
        <Button onClick={() => navigate("/app/settings/specialties/new")}>
          <Plus className="h-4 w-4 mr-2" />
          {t.new}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-neutral-400">
            {t.loading}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-2xl border-neutral-200">
            <p className="text-neutral-500">{t.none}</p>
          </div>
        ) : (
          items.map((item) => (
            <SectionCard 
              key={item.id}
              header={
                <div className="flex items-center justify-between w-full pr-2">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-neutral-100 flex items-center justify-center text-neutral-600">
                      {item.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-semibold text-neutral-800">{item.name}</span>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => deleteSpecialty(item.id)} className="text-red-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              }
            >
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end border-b pb-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-neutral-400 uppercase tracking-wider">
                      {lang === 'pt' ? "Sala Padrão" : "Default Room"}
                    </Label>
                    <div className="relative">
                      <Select 
                        value={item.room_id ? String(item.room_id) : "none"} 
                        onValueChange={(val) => updateRoom(item.id, val)}
                      >
                        <SelectTrigger className="h-10 px-3 rounded-lg border bg-neutral-50/50 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none">
                          <SelectValue placeholder={lang === 'pt' ? "Sem sala vinculada" : "No room assigned"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{lang === 'pt' ? "Sem sala vinculada" : "No room assigned"}</SelectItem>
                          {rooms.map(r => (
                            <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-emerald-600 text-xs font-bold uppercase tracking-wider pb-2">
                    <TrendingUp className="h-3.5 w-3.5" />
                    {lang === 'pt' ? "Tabela de Preços Sugeridos" : "Suggested Pricing Table"}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map(freq => (
                    <div key={freq} className="space-y-1.5">
                      <Label className="text-[10px] text-neutral-400 uppercase font-bold">
                        {freq}x {lang === 'pt' ? "por semana" : "per week"}
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">R$</span>
                        <Input 
                          value={prices[item.id]?.[freq] || prices["default"]?.[freq] || ""}
                          onChange={(e) => handlePriceChange(String(item.id), freq, e.target.value)}
                          className="pl-9 h-10 bg-neutral-50/50 focus:bg-white transition-colors"
                          placeholder="0,00"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          ))
        )}
      </div>

      <div className="flex items-center justify-end pt-6 border-t">
        <Button size="lg" onClick={savePrices} className="px-8">
          <Save className="h-4 w-4 mr-2" />
          {lang === 'pt' ? "Salvar Todas as Configurações" : "Save All Configurations"}
        </Button>
      </div>
    </div>
  );
}
